import { useId, useRef, useState } from 'preact/hooks';
import type { Pergunta } from '../types';
import { avisar } from '../ui';
import type { ItemFila } from './armazem';
import { CANAIS, FAIXAS, MAX_TEXTO, SEXOS, montarPayload, novoIdLocal, rascunhoVazio, validarRascunho, type Canal, type Rascunho } from './regras';
import { guardarResposta, pendentes } from './sync';

export type PesquisaAberta = {
  id: string;
  titulo: string;
  perguntaAncora: string;
  questionario: Pergunta[];
  periodoInicio: string;
  periodoFim: string;
  municipios: { codIbge: string; nome: string; meta: number; coletadasPorMim: number; coletadasNoMunicipio: number }[];
};

type Opcao<T> = { valor: T; rotulo: string };

/** Grupo de botões grandes de escolha única (acessível por teclado e leitor de tela: são botões de rádio de verdade). */
function Opcoes<T extends string | number | boolean>({ nome, legenda, valores, atual, onEscolher, grade, dica }: {
  nome: string;
  legenda: string;
  valores: Opcao<T>[];
  atual: T | null | undefined;
  onEscolher: (v: T) => void;
  grade?: boolean;
  dica?: string;
}) {
  return (
    <fieldset class="grupo">
      <legend>{legenda}</legend>
      {dica && <p class="dica">{dica}</p>}
      <div class={`opcoes${grade ? ' grade' : ''}`}>
        {valores.map((v) => (
          <label key={String(v.valor)} class={`opcao${atual === v.valor ? ' marcada' : ''}`}>
            <input type="radio" name={nome} value={String(v.valor)} checked={atual === v.valor} onChange={() => onEscolher(v.valor)} />
            <span>{v.rotulo}</span>
          </label>
        ))}
      </div>
    </fieldset>
  );
}

const NOTAS_0_10: Opcao<number>[] = Array.from({ length: 11 }, (_, i) => ({ valor: i, rotulo: String(i) }));
const ESCALA_1_5: Opcao<number>[] = [
  { valor: 1, rotulo: '1 · Muito ruim' },
  { valor: 2, rotulo: '2 · Ruim' },
  { valor: 3, rotulo: '3 · Regular' },
  { valor: 4, rotulo: '4 · Bom' },
  { valor: 5, rotulo: '5 · Muito bom' },
];

type Props = {
  pesquisa: PesquisaAberta;
  municipio: PesquisaAberta['municipios'][number];
  usuarioId: string;
  /** Canal da última resposta: quem entrevista em série quase sempre repete o canal. */
  canalInicial: Canal | '';
  onCanal: (c: Canal) => void;
  onVoltar: () => void;
};

export function Formulario({ pesquisa, municipio, usuarioId, canalInicial, onCanal, onVoltar }: Props) {
  const uid = useId();
  const [r, setR] = useState<Rascunho>(() => rascunhoVazio(canalInicial));
  const [erros, setErros] = useState<string[]>([]);
  const [salvando, setSalvando] = useState(false);
  const [salvas, setSalvas] = useState(0);
  const caixaErro = useRef<HTMLDivElement>(null);

  const definir = (parcial: Partial<Rascunho>) => setR((a) => ({ ...a, ...parcial }));
  const responder = (id: string, v: unknown) => setR((a) => ({ ...a, respostas: { ...a.respostas, [id]: v } }));

  async function salvar(e: Event) {
    e.preventDefault();
    if (salvando) return; // duplo toque não pode gerar duas respostas
    const problemas = validarRascunho(pesquisa.questionario, r);
    setErros(problemas);
    if (problemas.length) {
      queueMicrotask(() => caixaErro.current?.focus());
      return;
    }
    setSalvando(true);
    const agora = new Date();
    const idLocal = novoIdLocal();
    const item: ItemFila = {
      idLocal,
      usuarioId,
      pesquisaId: pesquisa.id,
      pesquisaTitulo: pesquisa.titulo,
      municipioNome: municipio.nome,
      payload: montarPayload(municipio.codIbge, r, agora, idLocal),
      criadaEm: agora.toISOString(),
      estado: 'pendente',
      tentativas: 0,
    };
    try {
      await guardarResposta(item);
    } catch (err) {
      setErros([`Não foi possível guardar esta resposta no aparelho: ${(err as Error).message} Não feche o app; libere espaço e tente de novo.`]);
      setSalvando(false);
      queueMicrotask(() => caixaErro.current?.focus());
      return;
    }
    if (r.canal) onCanal(r.canal);
    avisar(`Resposta guardada. ${pendentes().length} para enviar.`);
    setSalvas((n) => n + 1);
    setR(rascunhoVazio(r.canal));
    setErros([]);
    setSalvando(false);
    window.scrollTo({ top: 0 });
  }

  const nome = (s: string) => `${uid}-${s}`;
  const total = municipio.coletadasNoMunicipio + pendentes().filter((i) => i.pesquisaId === pesquisa.id && i.payload.codIbge === municipio.codIbge).length;

  return (
    <form class="coleta-form" onSubmit={salvar} noValidate>
      <div class="coleta-form-topo">
        <button type="button" class="botao" onClick={onVoltar}>← Voltar</button>
        <div>
          <h2>{municipio.nome}</h2>
          <p class="dica">{pesquisa.titulo} · {total} de {municipio.meta} no município{salvas > 0 ? ` · ${salvas} guardada${salvas === 1 ? '' : 's'} agora` : ''}</p>
        </div>
      </div>

      {erros.length > 0 && (
        <div class="erro caixa-erro" role="alert" tabIndex={-1} ref={caixaErro}>
          <strong>Falta completar:</strong>
          <ul>{erros.map((m) => <li key={m}>{m}</li>)}</ul>
        </div>
      )}

      <Opcoes nome={nome('canal')} legenda="Como foi a entrevista?" valores={CANAIS} atual={r.canal || null} onEscolher={(v) => definir({ canal: v })} grade />

      <Opcoes nome={nome('nota')} legenda={pesquisa.perguntaAncora} dica="0 = nada satisfeito, 10 = totalmente satisfeito" valores={NOTAS_0_10} atual={r.notaAprovacao} onEscolher={(v) => definir({ notaAprovacao: v })} grade />

      {pesquisa.questionario.map((p) => {
        const legenda = `${p.texto}${p.obrigatoria ? '' : ' (opcional)'}`;
        const v = r.respostas[p.id];
        if (p.tipo === 'nota') return <Opcoes key={p.id} nome={nome(p.id)} legenda={legenda} valores={NOTAS_0_10} atual={v as number | undefined} onEscolher={(x) => responder(p.id, x)} grade />;
        if (p.tipo === 'escala') return <Opcoes key={p.id} nome={nome(p.id)} legenda={legenda} valores={ESCALA_1_5} atual={v as number | undefined} onEscolher={(x) => responder(p.id, x)} />;
        if (p.tipo === 'sim_nao') return <Opcoes key={p.id} nome={nome(p.id)} legenda={legenda} valores={[{ valor: true, rotulo: 'Sim' }, { valor: false, rotulo: 'Não' }]} atual={v as boolean | undefined} onEscolher={(x) => responder(p.id, x)} grade />;
        if (p.tipo === 'escolha') return <Opcoes key={p.id} nome={nome(p.id)} legenda={legenda} valores={(p.opcoes ?? []).map((o) => ({ valor: o, rotulo: o }))} atual={v as string | undefined} onEscolher={(x) => responder(p.id, x)} />;
        return (
          <label key={p.id} class="grupo">
            <span class="legenda-texto">{legenda}</span>
            <textarea rows={3} maxLength={MAX_TEXTO} value={(v as string | undefined) ?? ''} onInput={(e) => responder(p.id, e.currentTarget.value)} />
          </label>
        );
      })}

      <details class="grupo opcionais">
        <summary>Perfil da pessoa (opcional)</summary>
        <Opcoes nome={nome('faixa')} legenda="Faixa etária" valores={[{ valor: '', rotulo: 'Não informar' }, ...FAIXAS.map((f) => ({ valor: f as string, rotulo: f }))]} atual={r.faixaEtaria} onEscolher={(x) => definir({ faixaEtaria: x })} grade />
        <Opcoes nome={nome('sexo')} legenda="Sexo" valores={[{ valor: '', rotulo: 'Não informar' }, ...SEXOS]} atual={r.sexo} onEscolher={(x) => definir({ sexo: x })} grade />
      </details>

      <p class="dica">Não anote nome, telefone ou documento: a pesquisa é anônima.</p>
      <div class="coleta-barra-acao">
        <button type="submit" class="botao primario grande" disabled={salvando}>{salvando ? 'Guardando…' : 'Guardar resposta'}</button>
      </div>
    </form>
  );
}
